import { Project } from "projen/lib";
import { TurboRepo } from "../../turbo/turbo-repo";
import { AgentRuleBundle } from "../agent-config-options";
import { AGENT_RULE_SCOPE } from "../types";
import { DEFAULT_BUILD_POLICY, ResolvedBuildPolicy } from "./build-policy";
import { hasComponent } from "./utils";

/**
 * Renders the caching bullet of the task-pipeline section.
 *
 * The bundle used to state "Uses remote caching (requires AWS
 * credentials)" for every consumer. That is false wherever
 * `remoteCacheOptions` is not configured — those projects cache
 * locally and need no credentials.
 */
function renderCachingBullet(policy: ResolvedBuildPolicy): string {
  if (policy.remoteCacheEnabled && policy.awsProfileName) {
    return `- Uses remote caching (requires AWS credentials on the \`${policy.awsProfileName}\` profile)`;
  }

  return "- Local caching only — no remote cache is configured, so no AWS credentials are required";
}

/**
 * Builds the Turborepo bundle — auto-detected when the TurboRepo
 * component is present.
 *
 * When `buildPolicy` is omitted the bundle ships the zero-remote-cache
 * defaults ({@link DEFAULT_BUILD_POLICY}).
 */
export function buildTurborepoBundle(
  buildPolicy: ResolvedBuildPolicy = DEFAULT_BUILD_POLICY,
): AgentRuleBundle {
  return {
    name: "turborepo",
    description: "Turborepo workspace rules and task pipeline conventions",
    appliesWhen: (project: Project) => hasComponent(project, TurboRepo),
    rules: [
      {
        name: "turborepo-conventions",
        description: "Turborepo build system and task pipeline conventions",
        scope: AGENT_RULE_SCOPE.FILE_PATTERN,
        filePatterns: ["turbo.json", "package.json"],
        content: [
          "# Turborepo Conventions",
          "",
          "## Build System",
          "",
          "- **Build**: `pnpm build:all` (uses Turborepo)",
          "- **Test**: `pnpm test` or `pnpm test:watch`",
          "- **Lint**: `pnpm eslint`",
          "",
          "## Task Pipeline",
          "",
          renderCachingBullet(buildPolicy),
          "- Only rebuilds changed packages",
          "- Cache key based on file hashes and dependency graph",
          "- Configured in `turbo.json`",
          "",
          "## Workspace Rules",
          "",
          "- Source files: `src/` directory",
          "- Tests: Co-located with source files (`.spec.ts` or `.test.ts`)",
          "- Exports: Use `index.ts` files for clean public APIs",
          "- Configuration: Managed by Projen (edit `.projenrc.ts` or `projenrc/*.ts`)",
        ].join("\n"),
        tags: ["workflow"],
      },
    ],
    claudePermissions: {
      allow: ["Bash(npx turbo:*)"],
    },
  };
}

/**
 * Turborepo bundle built with the default (no remote cache) build
 * policy. Preserved for backward compatibility with tests and
 * consumers that import the const directly. Prefer
 * `buildTurborepoBundle(buildPolicy)` when the consuming project's
 * Turborepo configuration is in scope.
 */
export const turborepoBundle: AgentRuleBundle = buildTurborepoBundle();
